import { useCallback, useEffect, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { DEFAULT_OPTION_TOPICS } from 'constants/general';

import Button from '../../../../components/Button/Button';
import Checkbox from '../../../../components/Checkbox/Checkbox';
import Select from '../../../../components/Select/Select';
import StatusDropButton from '../../../../components/StatusDropButton/StatusDropButton';
import {
  APPLY_COEFFICIENT, STAKEHOLDER, TEMPLATE, TOPIC,
  VALUE_CHAIN_SECTION,
} from '../../../../constants/formFields';
import { BUTTON_VARIANT } from '../../../../constants/interfaces';
import { MATERIAL_ASSESSMENT_RESPONDENT } from '../../../../constants/materialAssessment';

import type { IDashboardTopic } from '../../../../store/slices/survey/interfaces/IDashboardTopicsResponse';
import type { IInsightsDashboardFilters } from '../interfaces/IInsightsDashboardFilters';

import styles from '../scss/MAInsightsDashboardFilters.module.scss';

function InsightsDashboardFilters({
  control,
  templatesData,
  dashboardTopics,
  showActionButtons,
  onClearAll,
  onGenerate,
  setValue,
  vcOptions,
}: IInsightsDashboardFilters) {
  const { t } = useTranslation();
  const [dashboardTopicOptions, setDashboardTopicOptions] = useState<IDashboardTopic[]>([]);
  const templateValue = useWatch({ control, name: TEMPLATE });

  const handleSetOptions = useCallback(() => {
    if (dashboardTopics) {
      setDashboardTopicOptions([
        DEFAULT_OPTION_TOPICS,
        ...dashboardTopics,
      ]);
    }
  }, [dashboardTopics]);

  useEffect(() => {
    handleSetOptions();
  }, [handleSetOptions]);

  return (
    <div className={clsx(styles.filtersWrapper)}>
      <div className={styles.filters}>
        <Controller
          control={control}
          name={TEMPLATE}
          render={({ field: { name, value, onChange } }) => (
            <Select
              placeholder={t('materialityAssessment.template')}
              name={name}
              value={templatesData.find((option) => option.id === value) || null}
              className={styles.template}
              classNameMenu={styles.templateMenu}
              placeholderClassName={styles.templatePlaceholder}
              options={templatesData}
              onChange={(selectedOption: unknown) => {
                const option = selectedOption as any | null;
                onChange(option?.id);
                setValue(TOPIC, undefined);
                setValue(STAKEHOLDER, []);
              }}
              formatOptionLabel={(option) => (
                <div className={clsx(styles.answerTypeOption, option.id === value
                  ? styles.templateOptionSelected : undefined)}
                >
                  <span>{option.label}</span>
                </div>
              )}
            />
          )}
        />
        <Controller
          control={control}
          name={TOPIC}
          render={({ field: { name, value, onChange } }) => (
            <Select
              placeholder={t('materialityAssessment.topic')}
              name={name}
              value={(Array.isArray(dashboardTopicOptions) && value !== undefined)
                ? dashboardTopicOptions.find((option: IDashboardTopic) => option.id === value) || null
                : null}
              className={styles.topic}
              placeholderClassName={styles.topicPlaceholder}
              classNameMenu={styles.topicMenu}
              maxMenuHeight={200}
              onChange={(selectedOption: unknown) => {
                const option = selectedOption as any | null;
                onChange(option?.id);
                setValue(STAKEHOLDER, []);
              }}
              isDisabled={!templateValue}
              options={dashboardTopicOptions || []}
              required
            />
          )}
        />
        <Controller
          control={control}
          name={STAKEHOLDER}
          render={({ field: { value, onChange } }) => (
            <StatusDropButton
              options={Object.keys(MATERIAL_ASSESSMENT_RESPONDENT)}
              title={t('materialityAssessment.stakeholder')}
              className={styles.stakeholder}
              classNameList={styles.stakeholderList}
              onSelectionChange={onChange}
              selectedOptions={value?.filter((item): item is string => item !== undefined)}
              clearAllSelected={() => onChange([])}
              addAllSelected={() => onChange(Object.keys(MATERIAL_ASSESSMENT_RESPONDENT))}
              showSelectAll={value?.length === 0 || !value}
              isDisabled={!templateValue}
              translate
              hideSearch
            />
          )}
        />
        <Controller
          control={control}
          name={VALUE_CHAIN_SECTION}
          render={({ field: { name, value, onChange } }) => (
            <Select
              name={name}
              placeholder={t('common.selectVC')}
              options={vcOptions}
              value={value.label ? value : null}
              onChange={onChange}
              classNameMenu={styles.selectVC}
              maxMenuHeight={200}
              className={styles.template}
              placeholderClassName={styles.templatePlaceholder}
              isDisabled={!templateValue}
            />
          )}
        />
        <Controller
          control={control}
          name={APPLY_COEFFICIENT}
          render={({ field: { name, value, onChange } }) => (
            <Checkbox
              name={name}
              value={String(value)}
              label={t('materialityAssessment.applyGroupCoefficients')}
              checked={value || false}
              className={styles.checkbox}
              disabled={!templateValue}
              onChange={() => onChange(!value)}
            />
          )}
        />
      </div>
      {showActionButtons ? (
        <div className={styles.buttonWrapper}>
          <Button
            title={t('materialityAssessment.clearAll')}
            variant={BUTTON_VARIANT.ghost}
            className={styles.button}
            onClick={onClearAll}
          />
          <Button
            title={t('materialityAssessment.generate')}
            className={styles.button}
            onClick={onGenerate}
          />
        </div>
      ) : null}
    </div>
  );
}

export default InsightsDashboardFilters;
